import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CropField } from '@next/arable-lib/crop-field';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

import { loadCropVarietiesByCropIdRequest } from 'src/app/advices/store/actions';
import { selectCropVarietiesByCropId } from 'src/app/advices/store/selectors';

import { SiteState } from 'src/app/shared/models';

import { TranslatePipe } from '@next/core-lib/i18n';
import { LoadingCardComponent } from '../../../shared/components/loading-card.component/loading-card.component';
// eslint-disable-next-line max-len
import { SpecificFormHeaderSectionComponent } from '../../components/questionnaire/form-header-section/specific-form-header-section/specific-form-header-section.component';
import { RiskAnalysisComponent } from '../../components/risk-analysis/risk-analysis.component';
import { Advice } from '../../shared/constants';
import { DisplayAdvice, TgaCropFieldWithStatus } from '../../shared/models/models';
import { TgaSharedService } from '../../shared/services/tga-shared.service';
import { fetchGeneralAdvice, fetchVarietyPropertiesByEdiCode, loadCropFields } from '../../store/tga.actions';
import { selectCropFieldById, selectGeneralAdvice, getTgaCropYear } from '../../store/tga.selectors';

@Component({
  selector: 'app-tga-general-advice',
  templateUrl: './tga-general-advice.component.html',
  styleUrls: ['./tga-general-advice.component.scss'],
  imports: [SpecificFormHeaderSectionComponent, RiskAnalysisComponent, LoadingCardComponent, TranslatePipe],
})
export class TgaGeneralAdviceComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly tgaSharedService = inject(TgaSharedService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  generalAdvice: DisplayAdvice[] = [];
  cropFieldId?: number;
  cropYear = new Date().getFullYear();
  cropField: CropField | null = null;
  isLoading = true;
  cropVarietyName = '';
  translationParams: Record<string, string> = {};

  private readonly destroy$ = new Subject<void>();
  private readonly params$ = this.route.params;
  private readonly generalAdvice$ = this.store.select(selectGeneralAdvice);

  ngOnInit(): void {
    this.store
      .select(getTgaCropYear)
      .pipe(take(1))
      .subscribe((cropYear) => {
        this.cropYear = cropYear;
        this.initializeParams();
        this.ai.trackEvent(Advice.generalAdviceViewed, { cropField: this.cropFieldId });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeParams(): void {
    this.params$
      .pipe(
        tap((params: { cropFieldId?: string | number; cropYear?: string | number }) => {
          if (params.cropFieldId != null) this.cropFieldId = Number(params.cropFieldId);
        }),
        switchMap((params) => this.store.select(selectCropFieldById(Number(params.cropFieldId)))),
        tap((cropField) => this.handleCropFieldResult(cropField)),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.handleGeneralAdviceLoading();
  }

  private handleGeneralAdviceLoading(): void {
    combineLatest([this.params$, this.generalAdvice$])
      .pipe(
        tap(([params, generalAdvice]) => {
          const currentCropFieldId = Number(params.cropFieldId);
          const currentCropYear = this.cropYear;

          const needsToFetchAdvice =
            generalAdvice?.cropFieldId !== currentCropFieldId || generalAdvice?.cropYear !== currentCropYear;

          if (needsToFetchAdvice && currentCropFieldId) {
            this.isLoading = true;
            this.store.dispatch(
              fetchGeneralAdvice({
                cropFieldId: currentCropFieldId,
                cropYear: currentCropYear,
              }),
            );
          } else if (generalAdvice) {
            this.generalAdvice = generalAdvice.data!;
            this.isLoading = false;
          }

          this.changeDetectorRef.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private handleCropFieldResult(cropFieldWithStatus: TgaCropFieldWithStatus | null): void {
    if (!cropFieldWithStatus) {
      this.store.dispatch(loadCropFields({ cropYear: this.cropYear }));
      return;
    }

    this.cropField = cropFieldWithStatus.cropField;

    if (!cropFieldWithStatus.cropField.cropId) {
      return;
    }

    this.store
      .select(selectCropVarietiesByCropId)
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        tap((cropVarietiesState) => {
          const cropVarietyData = cropVarietiesState?.find((cv) => cv.cropId === cropFieldWithStatus.cropField.cropId);
          if (!cropVarietyData) {
            this.store.dispatch(
              loadCropVarietiesByCropIdRequest({
                cropIds: [cropFieldWithStatus.cropField.cropId],
              }),
            );
            return;
          }

          const varieties = cropVarietyData?.cropVarieties || [];

          if (varieties.length > 0) {
            const matchingVariety = varieties.find(
              (variety) => variety.id === cropFieldWithStatus.cropField.cropVarietyId,
            );
            if (matchingVariety) {
              this.cropVarietyName = matchingVariety.name;

              // Fetch variety properties using ediCode
              if (matchingVariety.ediCode) {
                this.store.dispatch(
                  fetchVarietyPropertiesByEdiCode({
                    ediCode: matchingVariety.ediCode,
                  }),
                );
              }
            }
          }
        }),
      )
      .subscribe();
  }

  getEndDateYear(cropField?: CropField): number | null {
    if (!cropField) return this.cropYear;
    return this.tgaSharedService.getEndDateYear(cropField, this.cropYear);
  }
}
